import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import colors from "../../../assets/colors";
import {
  createClientWorkoutTemplateRecurringEventDefinition,
  createWorkoutCalendarItem,
  updateRecurringEventDefinition,
} from "../../../redux/actions/Calendar";
import { StoreState } from "../../../redux/reducers";
import {
  CalendarItem,
  ClientTrainingPlan,
  ClientWorkoutGroupTemplate,
  ClientWorkoutTemplate,
  MutationCreateClientWorkoutTemplateRecurringEventDefinitionArgs,
  MutationCreateWorkoutCalendarItemArgs,
  MutationUpdateRecurringEventDefinitionArgs,
  RecurringEventDefinition,
  RecurringWorkoutCalendarItem,
} from "../../../types/gqlTypes";
import RecurringMenu from "../../Calendar/RecurringMenu";
import Checkbox from "../../Icons/Checkbox";
import SimpleButton from "../../SimpleButton";
import Footer from "../Footer";
import Header, { HeaderType } from "../Header";
import WorkoutGroupTemplateList from "./WorkoutGroupTemplateList";
import WorkoutTemplateCard from "./WorkoutTemplateCard";
import WorkoutTemplateList from "./WorkoutTemplateList";

interface OwnProps {
  onClose: () => void;
  onBack: () => void;
  day: Date;
  calendarItem?: CalendarItem;
}
interface Props extends OwnProps {
  createWorkoutCalendarItem: (
    args: MutationCreateWorkoutCalendarItemArgs
  ) => void;
  createRecurringWorkout: (
    args: MutationCreateClientWorkoutTemplateRecurringEventDefinitionArgs
  ) => void;
  updateRecurringEventDefinition: (
    args: MutationUpdateRecurringEventDefinitionArgs
  ) => void;
  clientPlans: ClientTrainingPlan[];
}

const WorkoutEvent = (props: Props) => {
  const {
    clientPlans,
    day,
    calendarItem,
    onClose,
    onBack,
    createWorkoutCalendarItem,
    createRecurringWorkout,
    updateRecurringEventDefinition,
  } = props;
  const { id: clientId } = useParams();

  const [selectedWorkoutGroupTemplate, setSelectedWorkoutGroupTemplate] =
    useState(null as ClientWorkoutGroupTemplate);
  const [selectedWorkoutTemplate, setSelectedWorkoutTemplate] = useState(
    null as ClientWorkoutTemplate
  );
  const [repeat, setRepeat] = useState(false);
  const [recurringEvent, setRecurringEvent] =
    useState<RecurringEventDefinition>({
      RRule: "",
      endDate: format(day, "yyyy-MM-dd"),
      startDate: format(day, "yyyy-MM-dd"),
    });
  const isUpdating = !!calendarItem;

  useEffect(() => {
    if (calendarItem) {
      setRepeat(true);
      setSelectedWorkoutTemplate(
        (calendarItem as RecurringWorkoutCalendarItem)?.clientWorkoutTemplate
      );
      setRecurringEvent({
        RRule: calendarItem?.recurringEventDefinition?.RRule,
        endDate: calendarItem?.recurringEventDefinition?.endDate,
        startDate: calendarItem?.recurringEventDefinition?.startDate,
      });
    }
  }, [calendarItem]);

  const onBackClick = () => {
    if (calendarItem) {
      onClose();
    } else if (selectedWorkoutTemplate) {
      setSelectedWorkoutTemplate(null);
      setRepeat(false);
    } else if (selectedWorkoutGroupTemplate) {
      setSelectedWorkoutGroupTemplate(null);
    } else {
      onBack();
    }
  };
  const onSuccess = () => {
    if (repeat && selectedWorkoutTemplate) {
      const ind = recurringEvent.RRule.indexOf("RRULE:");
      if (isUpdating) {
        updateRecurringEventDefinition({
          clientId,
          rRule: recurringEvent.RRule.slice(ind + 6),
          recurringEventDefinitionId:
            calendarItem?.recurringEventDefinition?.id,
          endDate: recurringEvent.endDate,
        });
      } else {
        createRecurringWorkout({
          clientId,
          clientWorkoutTemplateId: selectedWorkoutTemplate.id,
          rRule: recurringEvent.RRule.slice(ind + 6),
          startDate: recurringEvent.startDate,
          endDate: recurringEvent.endDate,
        });
      }
    } else if (selectedWorkoutTemplate) {
      const [year, month, dayOfMonth] = format(day, "yyyy-MM-dd").split("-");
      createWorkoutCalendarItem({
        clientId,
        clientWorkoutTemplateId: selectedWorkoutTemplate.id,
        dayOfMonth: Number(dayOfMonth),
        month: Number(month),
        year: Number(year),
      });
    }
    onClose();
  };

  const repeatStyle: React.CSSProperties = {
    fontSize: "16px",
    lineHeight: "16px",
    letterSpacing: "-0.022em",
    color: colors.caliber_gray_text,
    marginLeft: 8,
    marginBottom: 24,
  };
  const workoutCardStyle: React.CSSProperties = {
    marginTop: 42,
    marginBottom: 24,
  };

  return (
    <div className="d-flex flex-column" style={{}}>
      <Header
        day={day}
        type={HeaderType.STRENGTH_WORKOUT}
        onBackClick={onBackClick}
      />

      {!selectedWorkoutGroupTemplate && (
        <WorkoutGroupTemplateList
          workoutGroupTemplates={
            clientPlans.find((plan) => plan?.active)
              ?.clientWorkoutGroupTemplates
          }
          onSelect={(workoutGroupTemplate) =>
            setSelectedWorkoutGroupTemplate(workoutGroupTemplate)
          }
        />
      )}

      {selectedWorkoutGroupTemplate && !selectedWorkoutTemplate && (
        <WorkoutTemplateList
          workoutTemplates={selectedWorkoutGroupTemplate.clientWorkoutTemplates}
          onSelect={(workoutTemplate) =>
            setSelectedWorkoutTemplate(workoutTemplate)
          }
        />
      )}

      {selectedWorkoutTemplate && (
        <div style={workoutCardStyle}>
          <WorkoutTemplateCard workoutTemplate={selectedWorkoutTemplate} />
        </div>
      )}

      {selectedWorkoutTemplate && (
        <div className="d-flex">
          <Checkbox
            disabled={isUpdating}
            checked={repeat}
            onClick={() => setRepeat(!repeat)}
          />
          <SimpleButton
            disabled={isUpdating}
            onClick={() => setRepeat(!repeat)}
            nofocus
            className="bold"
            style={repeatStyle}
          >
            Repeat
          </SimpleButton>
        </div>
      )}

      {repeat && (
        <RecurringMenu
          day={day}
          recurringEvent={recurringEvent}
          onChange={(recurringEvent) => {
            setRecurringEvent(recurringEvent);
          }}
        />
      )}

      {selectedWorkoutTemplate && (
        <Footer
          successText={calendarItem ? "Update activity" : "Add activity"}
          onSuccess={onSuccess}
          onCancel={() => {
            if (calendarItem) {
              onClose();
            } else {
              onBack();
            }
          }}
        />
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  createWorkoutCalendarItem: (args: MutationCreateWorkoutCalendarItemArgs) => {
    dispatch(createWorkoutCalendarItem(args));
  },
  createRecurringWorkout: (
    args: MutationCreateClientWorkoutTemplateRecurringEventDefinitionArgs
  ) => {
    dispatch(createClientWorkoutTemplateRecurringEventDefinition(args));
  },
  updateRecurringEventDefinition: (
    args: MutationUpdateRecurringEventDefinitionArgs
  ) => {
    dispatch(updateRecurringEventDefinition(args));
  },
});

const mapStateToProps = (state: StoreState, ownProps: OwnProps) => ({
  clientPlans: state.clientPlansState.clientPlans,
  onClose: ownProps.onClose,
  onBack: ownProps.onBack,
  day: ownProps.day,
  calendarItem: ownProps.calendarItem,
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkoutEvent);
